import React, { useEffect, useRef, useState } from "react";
import { imgUrl } from "@/lib/api";

/**
 * Canvas frame-sequence turntable.
 * - Auto-detects tier from frame count:
 *   ≥24 frames → full drag-scrub
 *    6-12       → interpolated micro-rotation (auto continuous loop when idle)
 *    1-3        → parallax breathing plinth (ping-pong when idle)
 * - Progressive loading:
 *    - plinth / micro tiers: eager-load ALL frames so auto-loop never skips
 *    - scrub tier: first frame instant, remainder queued on idle
 * - IntersectionObserver pauses the RAF loop when the card is off-screen so
 *   26 turntables on the shop page don't fry the CPU.
 */
const Turntable = ({ frames = [], alt = "" }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const framesRef = useRef([]); // HTMLImageElements
  const [loaded, setLoaded] = useState(0);
  const [idx, setIdx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [visible, setVisible] = useState(true);
  const startX = useRef(0);
  const startIdx = useRef(0);

  const tier =
    frames.length >= 24 ? "scrub" : frames.length >= 6 ? "micro" : "plinth";
  const total = frames.length;

  // ---- Load frames ----
  useEffect(() => {
    let cancelled = false;
    framesRef.current = new Array(frames.length).fill(null);

    const loadIdx = (i) =>
      new Promise((res) => {
        if (!frames[i]) return res(false);
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => {
          if (cancelled) return;
          framesRef.current[i] = im;
          setLoaded((n) => n + 1);
          res(true);
        };
        im.onerror = () => res(false);
        im.src = imgUrl(frames[i]);
      });

    (async () => {
      if (frames.length === 0) return;
      // Always show the first frame ASAP
      await loadIdx(0);
      draw(0);
      if (tier === "scrub") {
        // idle-load the rest for scrub tier
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 60));
        for (let i = 1; i < frames.length; i++) idle(() => loadIdx(i));
      } else {
        // eager-load remaining frames in parallel — tiny cost, avoids skipping
        await Promise.all(frames.slice(1).map((_, i) => loadIdx(i + 1)));
      }
    })();

    return () => { cancelled = true; };
  }, [JSON.stringify(frames)]);

  const draw = (i) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); }
    ctx.clearRect(0, 0, w, h);

    let frameIndex = i;
    // fall back to nearest loaded frame if the target hasn't arrived yet
    let im = framesRef.current[frameIndex];
    if (!im) {
      for (let d = 1; d < frames.length; d++) {
        if (framesRef.current[(frameIndex + d) % frames.length]) { im = framesRef.current[(frameIndex + d) % frames.length]; break; }
        if (framesRef.current[(frameIndex - d + frames.length) % frames.length]) { im = framesRef.current[(frameIndex - d + frames.length) % frames.length]; break; }
      }
    }
    if (!im) return;

    const iw = im.naturalWidth, ih = im.naturalHeight;
    const scale = Math.min(w / iw, h / ih);
    const dw = iw * scale, dh = ih * scale;
    const dx = (w - dw) / 2, dy = (h - dh) / 2;
    const lean = tier === "plinth" ? (idx - (total - 1) / 2) * 8 : 0;
    ctx.save();
    ctx.translate(dx + lean, dy);
    ctx.drawImage(im, 0, 0, dw, dh);
    ctx.restore();
  };

  useEffect(() => { draw(idx); }, [idx, loaded]);

  useEffect(() => {
    const on = () => draw(idx);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [idx]);

  // ---- IntersectionObserver: only animate when on screen ----
  useEffect(() => {
    if (!wrapRef.current || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, []);

  // ---- Drag ----
  const onDown = (e) => {
    setDragging(true);
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    startX.current = x;
    startIdx.current = idx;
  };
  const onMove = (e) => {
    if (!dragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = x - startX.current;
    const w = wrapRef.current ? wrapRef.current.clientWidth : 1;
    const step = Math.round((dx / w) * (total - 1) * 1.3);
    let next = startIdx.current - step;
    if (tier === "plinth") {
      next = Math.max(0, Math.min(total - 1, next));
    } else {
      next = ((next % total) + total) % total;
    }
    setIdx(next);
  };
  const onUp = () => setDragging(false);

  // ---- Auto rotation ----
  // plinth: ping-pong between 0 and total-1
  // micro / scrub: continuous forward rotation
  useEffect(() => {
    if (total < 2 || !visible) return;
    let raf;
    const t0 = performance.now();
    const period = tier === "plinth" ? 6 : tier === "micro" ? 8 : 20; // seconds per cycle
    const loop = (t) => {
      if (!dragging) {
        const dt = (t - t0) / 1000;
        if (tier === "plinth") {
          const phase = (Math.sin((dt / period) * Math.PI * 2) + 1) / 2;
          setIdx(Math.round(phase * (total - 1)));
        } else {
          const step = Math.floor((dt / period) * total) % total;
          setIdx(step);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [tier, total, dragging, visible]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full select-none touch-none cursor-grab active:cursor-grabbing"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      data-testid="turntable"
      data-testid-tier={tier}
      data-testid-frames={total}
      role="img"
      aria-label={alt}
    >
      <canvas ref={canvasRef} className="w-full h-full" />
      {total > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-40 h-[1px]" style={{ background: "var(--stone)" }}>
          <div
            className="h-[3px] -mt-[1px]"
            style={{ width: `${((idx + 1) / total) * 100}%`, background: "var(--ink)", transition: "width 220ms ease" }}
          />
        </div>
      )}
    </div>
  );
};

export default Turntable;
